import React, {useEffect, useCallback, useMemo, memo, Fragment} from 'react';
import {Props} from './ac-simple-pagination.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {ICONS, SIZES, THEMES} from '@asdf-constants';

// Imports => Utilities
import {AcIsSet, AcIsFunction} from '@asdf-utils';

// Imports => Atoms
import {AcIcon, AcRipple} from '@asdf-atoms';

const _CLASSES = {
  MAIN: 'ac-pagination',
  FROM_TO: 'ac-pagination__from-to',
  NAV: 'ac-pagination__nav',
  LIST: 'ac-pagination__list',
  ITEM: 'ac-pagination__list__item',
  LINK: {
    MAIN: 'ac-pagination__link',
    ACTIVE: 'ac-pagination__link--active',
    TRANSPARENT: 'ac-pagination__link--transparent',
    DISABLED: 'ac-pagination__link--disabled',
  },
};

const AcSimplePagination = memo(
  ({
    current_page = 0,
    from,
    to,
    showFromTo = false,
    callback,
    className,
    linkNext,
    translations = {
      'go-to-first-page': 'Go to first page',
      'go-to-previous-page': 'Go to previous page',
      'go-to-next-page': 'Go to next page',
      page: 'page',
      'current-page': 'current page',
      'pagination-navigation': 'pagination navigation',
      showing: 'showing',
      of: 'of',
      many: 'many',
      results: 'results',
    },
    ...rest
  }) => {
    const handlePaginationItemClick = (event, num) => {
      if (event.preventDefault) {
        event.preventDefault();
      }
      if (event.stopPropagation) {
        event.stopPropagation();
      }

      if (num === current_page) {
        return;
      }
      if (num < 1) {
        return;
      }
      if (num > current_page && linkNext === null) {
        return;
      }

      if (AcIsSet(callback) && AcIsFunction(callback)) {
        callback(event, num);
      }
    };

    const handleKeyUp = event => {
      if (!AcIsSet(event)) {
        return;
      }
      if (event && event.persist) {
        event.persist();
      }
      const key = event.key || event.which;

      const $active_element = document.activeElement;

      const $inputs = ['input', 'select', 'button', 'textarea', 'form'];

      if (key) {
        if ($active_element && $inputs.indexOf($active_element.tagName.toLowerCase()) === -1) {
          switch (key) {
            case 'ArrowLeft':
            case 37:
              handlePaginationItemClick(event, current_page - 1);
              break;

            case 'ArrowRight':
            case 39:
              handlePaginationItemClick(event, current_page + 1);
              break;

            default:
          }
        }
      }
    };

    const addEvents = () => {
      document.addEventListener('keyup', handleKeyUp, false);
    };

    const removeEvents = () => {
      document.removeEventListener('keyup', handleKeyUp, false);
    };

    useEffect(() => {
      addEvents();

      return () => removeEvents();
    }, []);

    const getLinkClassNames = useCallback(
      (active = false, disabled = false, transparent = false) => {
        return clsx(
          _CLASSES.LINK.MAIN,
          active && _CLASSES.LINK.ACTIVE,
          disabled && _CLASSES.LINK.DISABLED,
          transparent && _CLASSES.LINK.TRANSPARENT
        );
      },
      []
    );

    const getListItemClassNames = useMemo(() => {
      return clsx(_CLASSES.ITEM);
    }, []);

    const getListClassNames = useMemo(() => {
      return clsx(_CLASSES.LIST);
    }, []);

    const getNavClassNames = useMemo(() => {
      return clsx(_CLASSES.NAV);
    }, []);

    const getFromToClassNames = useMemo(() => {
      return clsx(_CLASSES.FROM_TO);
    }, []);

    const getMainClassNames = useMemo(() => {
      return clsx(_CLASSES.MAIN, className);
    }, [className]);

    const renderFirstPageItem = useMemo(() => {
      const disabled = current_page === 1;

      const props = {
        href: '#',
        onClick: disabled ? null : event => handlePaginationItemClick(event, 1),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-first-page'],
        title: translations['go-to-first-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li
          className={`${getListItemClassNames} h-hidden-xxs h-hidden-xs`}
          rel={`ac-pagination__item--first`}
        >
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_LEFT_START} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [handlePaginationItemClick, getLinkClassNames, getListItemClassNames, current_page]);

    const renderPreviousPageItem = useMemo(() => {
      const disabled = current_page === 1;

      const props = {
        href: '#',
        onClick: disabled ? null : event => handlePaginationItemClick(event, current_page - 1),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-previous-page'],
        title: translations['go-to-previous-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li className={`${getListItemClassNames}`} rel={`ac-pagination__item--previous`}>
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_LEFT} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [handlePaginationItemClick, getLinkClassNames, getListItemClassNames, current_page]);

    const renderNextPageItem = useMemo(() => {
      const disabled = linkNext === null;

      const props = {
        href: '#',
        onClick: disabled ? null : event => handlePaginationItemClick(event, current_page + 1),
        className: getLinkClassNames(false, disabled, true),
        'aria-label': translations['go-to-next-page'],
        title: translations['go-to-next-page'],
        'aria-disabled': disabled,
        disabled,
      };

      const object = (
        <li className={`${getListItemClassNames}`} rel={`ac-pagination__item--next`}>
          <a {...props}>
            {!disabled && <AcRipple theme={THEMES.PITCH} size={SIZES.SMALL} simple />}
            <AcIcon icon={ICONS.CHEVRON_RIGHT} alt={props.title} title={props.title} />
          </a>
        </li>
      );

      return object;
    }, [
      handlePaginationItemClick,
      getLinkClassNames,
      getListItemClassNames,
      current_page,
      linkNext,
    ]);

    const renderPaginationItems = useMemo(() => {
      if (!AcIsSet(current_page)) {
        return null;
      }
      let result = [];

      let label = `${translations.page} ${current_page}, ${translations['current-page']}`;

      const props = {
        href: '#',
        onClick: event => handlePaginationItemClick(event, current_page),
        className: getLinkClassNames(true),
        'aria-label': label,
        title: label,
      };

      props['aria-current'] = true;

      const object = (
        <li
          className={getListItemClassNames}
          key={`ac-pagination__item--${current_page}`}
          rel={`ac-pagination__item--${current_page}`}
        >
          <a {...props}>
            <div>{current_page}</div>
          </a>
        </li>
      );

      result.push(object);

      result.unshift(
        <Fragment key={`ac-pagination__item--previous`}>{renderPreviousPageItem}</Fragment>
      );
      result.unshift(<Fragment key={`ac-pagination__item--first`}>{renderFirstPageItem}</Fragment>);

      result.push(<Fragment key={`ac-pagination__item--next`}>{renderNextPageItem}</Fragment>);

      return result;
    }, [
      current_page,
      getLinkClassNames,
      getListItemClassNames,
      renderFirstPageItem,
      renderPreviousPageItem,
      renderNextPageItem,
    ]);

    const renderFromToResults = useMemo(() => {
      if (!showFromTo) {
        return null;
      }
      if (!AcIsSet(from) || !AcIsSet(to)) {
        return null;
      }

      const content = `${translations.showing} <strong>${from}</strong> - <strong>${to}</strong> ${translations.of} <strong>${translations.many}</strong> ${translations.results}`;

      return (
        <div
          className={getFromToClassNames}
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      );
    }, [showFromTo, from, to]);

    return (
      <div className={getMainClassNames} {...rest}>
        {renderFromToResults}
        <nav
          role={'navigation'}
          aria-label={`${translations['pagination-navigation']}`}
          className={getNavClassNames}
        >
          <ul className={getListClassNames}>{renderPaginationItems}</ul>
        </nav>
      </div>
    );
  }
);

AcSimplePagination.displayName = 'AcSimplePagination';
AcSimplePagination.propTypes = Props;

export {AcSimplePagination};
export default AcSimplePagination;
